export function multiplyPrecision(value: number, precision = 0): number {
  // Faster than log?
  const precisionMultiplier = +('1' + '0'.repeat(precision));
  return Math.round(value * precisionMultiplier);
}

export function parseNumberInput(value: string, precision = 0): number {
  const strValue = `${value}`.replace(/,+/g, '.');
  const parts = strValue.split('.');
  let inputValue = strValue;
  if (parts.length > 1) {
    const pointValue = parts.pop();
    inputValue = parts.join('') + '.' + pointValue;
  }
  const parsed = parseFloat(inputValue);
  const multiplied = multiplyPrecision(parsed, precision);
  if (isNaN(multiplied)) {
    throw new Error('String is not a number');
  }
  return multiplied;
}

/**
 * Formats a number to a string with a specified precision, replacing the decimal point with <decimalSeparator>.
 * Thousands are separated by <thousandSeparator>.
 * @param value The number to format.
 * @param precision The number of decimal places to include.
 * @param thousandSeparator The character to use for separating thousands (default is '.').
 * @param decimalSeparator The character to use for the decimal point (default is ',').
 * @returns The formatted number as a string.
 * @example
 * formatNumber(1234.5678, 2) // returns '1.234,57'
 * formatNumber(0.1, 2) // returns '0,10'
 */
export function formatNumber(
  value: number,
  precision: number,
  thousandSeparator: string = '.',
  decimalSeparator: string = ',',
): string {
  const stringRepresentation = value.toFixed(precision);

  // Split the string representation into whole and fractional parts
  const [whole, fractional] = stringRepresentation.split('.', 2);

  // Add dots to the whole part for thousands.
  const wholeParts = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
  // Ensure fractional part has the correct length
  const fractionalPart = fractional ? fractional.padEnd(precision, '0') : '0'.repeat(precision);

  return `${wholeParts}${fractional ? decimalSeparator + fractionalPart : ''}`;
}

/**
 * Decimates a number and formats it as a string with the specified decimal separator.
 * @param value The number to divide and represent as a string.
 * @param decimator The amount of times to decimate the number.
 * @param opts Options for formatting the number.
 * @returns A string representation of the decimated number.
 * @example
 * decimateAndFormatNumber(234, 3) // returns '0.234'
 * decimateAndFormatNumber(1, 2) // returns '0.01'
 * decimateAndFormatNumber(0, 2) // returns '0.00'
 * decimateAndFormatNumber(-99, 2) // returns '-0.99'
 */
export function decimateAndFormatNumber(
  value: number,
  decimator = 0,
  opts: {
    decimalSeperator?: string;
    removeTrailingZeros?: boolean;
  } = {},
): string {
  const { decimalSeperator = '.', removeTrailingZeros = false } = opts;
  const isNegative = value < 0;
  const asString = `${Math.abs(Math.round(value))}`;
  if (decimator > 0) {
    const firstNumbers = asString.slice(0, -decimator);
    let lastNumber = asString.slice(-decimator).padStart(decimator, '0');
    if (removeTrailingZeros) {
      while (lastNumber.endsWith('0')) {
        lastNumber = lastNumber.substring(0, lastNumber.length - 1);
      }
    }
    let result = `${isNegative ? '-' : ''}${firstNumbers || '0'}`;
    if (lastNumber.length > 0) {
      result += `${decimalSeperator}${lastNumber}`;
    }
    return result;
  } else {
    return isNegative ? '-' + asString : asString;
  }
}

export function splitEvenly(totalEuros: number, count: number): string[] {
  if (count === 0) return [];
  const each = Math.floor((totalEuros * 100) / count) / 100;
  const remainder = Math.round(totalEuros * 100) % count;
  return Array.from({ length: count }, (_, i) => (i < remainder ? (each + 0.01).toFixed(2) : each.toFixed(2)));
}

/**
 * Split a euro total across items proportionally by weight (e.g. sale amounts).
 * Remainder cents are assigned to items with the largest fractional parts.
 */
export function splitProRata(totalEuros: number, weights: number[]): string[] {
  if (weights.length === 0) return [];

  const totalWeight = weights.reduce((sum, w) => sum + w, 0);
  if (totalWeight <= 0) {
    return splitEvenly(totalEuros, weights.length);
  }

  const totalCents = Math.round(totalEuros * 100);
  const rawShares = weights.map((w) => (totalCents * w) / totalWeight);
  const baseCents = rawShares.map((share) => Math.floor(share));
  let remainder = totalCents - baseCents.reduce((sum, c) => sum + c, 0);

  const fractionalOrder = rawShares
    .map((share, index) => ({ index, fraction: share - Math.floor(share) }))
    .sort((a, b) => b.fraction - a.fraction);

  const finalCents = [...baseCents];
  for (const { index } of fractionalOrder) {
    if (remainder <= 0) break;
    finalCents[index] += 1;
    remainder -= 1;
  }

  return finalCents.map((cents) => (cents / 100).toFixed(2));
}

export function NaNToZero(value: number): number {
  if (isNaN(value)) {
    return 0;
  } else {
    return value;
  }
}

export function parseIntOrNull(value: string): number | null {
  const parsed = parseInt(value, 10);
  if (isNaN(parsed)) {
    return null;
  } else {
    return parsed;
  }
}

export function extractIntFromText(value: string): number {
  const parsed = parseInt(value.replace(/[^0-9]/g, ''), 10);
  if (isNaN(parsed)) {
    return 0;
  } else {
    return parsed;
  }
}
